/**
 * Editor Integration — document normalization.
 *
 * Brings a rendered document to exactly the shape EditorPage reads, so the
 * editor performs no conversion of its own: every page and element carries the
 * fields the editor expects, in the order it expects them.
 *
 * This prepares the document for editing. It does not edit it: nothing here
 * changes a design decision, moves anything, or restyles anything.
 *
 * Pure, deterministic, idempotent — normalizing twice changes nothing.
 */

import { EDITOR_DEFAULTS, GEOMETRYLESS_TYPES } from './sessionSchema';

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const isStr = (v) => typeof v === 'string' && v.length > 0;

/** One element, in the editor's native shape. */
const normalizeElement = (element, index, pageId) => {
  const el = isObj(element) ? element : {};
  const geometryless = GEOMETRYLESS_TYPES.includes(el.type);

  const normalized = {
    ...el,
    id: isStr(el.id) ? el.id : `${pageId}-el-${index + 1}`,
    type: el.type,
    rotation: isNum(el.rotation) ? el.rotation : EDITOR_DEFAULTS.rotation,
    opacity: isNum(el.opacity) ? el.opacity : EDITOR_DEFAULTS.opacity,
    visible: el.visible !== false,
    locked: el.locked === true,
    parentId: isStr(el.parentId) ? el.parentId : null,
  };

  if (!geometryless) {
    normalized.x = isNum(el.x) ? el.x : 0;
    normalized.y = isNum(el.y) ? el.y : 0;
    normalized.width = isNum(el.width) ? Math.max(0, el.width) : 0;
    normalized.height = isNum(el.height) ? Math.max(0, el.height) : 0;
  } else {
    // The editor recomputes group bounds lazily; a stale cache must not ship.
    delete normalized.derivedBounds;
  }

  // The editor reads text copy from `text`; older payloads used `content`.
  if (el.type === 'text' && typeof el.text !== 'string' && typeof el.content === 'string') {
    normalized.text = el.content;
  }

  return normalized;
};

/** One page, in the editor's native shape. */
const normalizePage = (page, index) => {
  const p = isObj(page) ? page : {};
  const id = isStr(p.id) ? p.id : `${index + 1}`;
  const elements = (Array.isArray(p.elements) ? p.elements : []).map((el, i) => normalizeElement(el, i, id));

  return {
    ...p,
    id,
    name: isStr(p.name) ? p.name : `Page ${index + 1}`,
    background: isStr(p.background) ? p.background : EDITOR_DEFAULTS.background,
    visible: p.visible !== false,
    locked: p.locked === true,
    // Element ORDER IS Z-ORDER, and the Rendering Engine already resolved it
    // (layer bands, then members below their group). Normalization maps each
    // element in place and must never reorder: doing so silently repaints the
    // design in the wrong stacking order.
    elements,
  };
};

/**
 * Normalize a rendered document into editor-native pages.
 *
 * @param {Object} document
 * @returns {{ pages: Object[], canvasSize: { width: number, height: number } }}
 */
export const normalizeEditorDocument = (document) => {
  const doc = isObj(document) ? document : {};
  const pages = (Array.isArray(doc.pages) ? doc.pages : []).map(normalizePage);

  return {
    pages: pages.length ? pages : [normalizePage({}, 0)],
    canvasSize: {
      width: isNum(doc.canvas_width) ? doc.canvas_width : EDITOR_DEFAULTS.canvas.width,
      height: isNum(doc.canvas_height) ? doc.canvas_height : EDITOR_DEFAULTS.canvas.height,
    },
  };
};

export const __internals = { normalizeElement, normalizePage };
